// Side drawer that lets the admin edit the selected branch (label, condition,
// action). Saves are local — the parent canvas keeps the in-memory branches
// list and writes them back via PUT when the user hits "Save Flow".
import React, { useEffect, useState } from 'react';
import { X, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BranchDrawer = ({
  branch, onChange, onDelete, onClose,
  actionOptions, connections, token,
  rules = [], currentRuleId = null,
}) => {
  const [whenExpr, setWhenExpr] = useState(branch.when_expr || '');
  const [exprStatus, setExprStatus] = useState({ valid: true, error: '' });

  useEffect(() => {
    setWhenExpr(branch.when_expr || '');
    // Intentionally only sync when the caller switches to a different branch —
    // typing into the box on the same branch shouldn't reset the value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch.id]);

  // Live syntax check via backend
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!whenExpr.trim()) { setExprStatus({ valid: true, error: '' }); return; }
      try {
        const r = await axios.post(
          `${API}/admin/voicefabric/check-when-expr`,
          { expr: whenExpr },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setExprStatus(r.data);
      } catch {
        setExprStatus({ valid: true, error: '' });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [whenExpr, token]);

  const update = (key, val) => onChange({ ...branch, [key]: val });

  const isCatalog = branch.action_type !== 'log' && branch.action_type !== 'webhook';
  const compatibleConns = connections.filter(c =>
    c.action_type === branch.action_type
  );

  return (
    <div
      className="fixed top-0 right-0 h-full w-[400px] bg-[#0A0F1C] border-l border-white/10 shadow-2xl shadow-black/60 z-50 overflow-y-auto"
      data-testid="branch-drawer"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 sticky top-0 bg-[#0A0F1C]">
        <h3 className="text-white font-semibold text-sm">Edit Branch</h3>
        <button
          onClick={onClose}
          className="text-white/40 hover:text-white"
          data-testid="branch-drawer-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 space-y-5">
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Label</label>
          <input
            value={branch.label || ''}
            onChange={e => update('label', e.target.value)}
            placeholder="Hot lead"
            className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50"
            data-testid="branch-label-input"
          />
        </div>

        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">When (DSL)</label>
          <textarea
            value={whenExpr}
            onChange={e => { setWhenExpr(e.target.value); update('when_expr', e.target.value); }}
            placeholder='intent == "callback" and confidence > 0.7'
            rows={3}
            className={`w-full mt-1.5 bg-white/5 border rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none ${
              exprStatus.valid ? 'border-white/10 focus:border-teal-500/50' : 'border-red-500/50'
            }`}
            data-testid="branch-when-expr"
          />
          {!exprStatus.valid && (
            <p className="text-red-300 text-[11px] mt-1">⚠ {exprStatus.error}</p>
          )}
          <p className="text-white/30 text-[11px] mt-1">
            Leave empty to use equality match below, or for the default branch.
          </p>
        </div>

        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Action</label>
          <select
            value={branch.action_type || 'log'}
            onChange={e => update('action_type', e.target.value)}
            className="w-full mt-1.5 bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50"
            data-testid="branch-action-select"
          >
            {Object.entries(
              actionOptions.reduce((acc, o) => {
                acc[o.group] = acc[o.group] || [];
                acc[o.group].push(o);
                return acc;
              }, {})
            ).map(([group, opts]) => (
              <optgroup key={group} label={group}>
                {opts.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.label}{o.status === 'stub' ? ' (coming soon)' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {isCatalog && (
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider">Connection</label>
            {compatibleConns.length === 0 ? (
              <p className="text-amber-300/70 text-xs mt-1.5">
                No connections for {branch.action_type}. Configure one under Integrations first.
              </p>
            ) : (
              <select
                value={branch.action_config?.connection_id || ''}
                onChange={e => update('action_config', {
                  ...(branch.action_config || {}),
                  connection_id: e.target.value,
                })}
                className="w-full mt-1.5 bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50"
                data-testid="branch-connection-select"
              >
                <option value="">— select connection —</option>
                {compatibleConns.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={branch.enabled !== false}
              onChange={e => update('enabled', e.target.checked)}
              className="accent-teal-500"
              data-testid="branch-enabled-toggle"
            />
            Branch enabled
          </label>
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 text-red-300 hover:text-red-200 text-xs px-2 py-1.5 rounded hover:bg-red-500/10"
            data-testid="branch-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete branch
          </button>
        </div>

        {/* Phase-4 chain hookup: pick another rule to evaluate after this branch fires */}
        <div className="pt-2 border-t border-white/5">
          <label className="text-white/50 text-xs uppercase tracking-wider">Then run rule (chain)</label>
          {rules.filter(r => r.id !== currentRuleId).length === 0 ? (
            <p className="text-white/30 text-[11px] mt-1.5">
              No other rules to chain to. Create another rule first.
            </p>
          ) : (
            <select
              value={branch.next_rule_id || ''}
              onChange={e => update('next_rule_id', e.target.value)}
              className="w-full mt-1.5 bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50"
              data-testid="branch-next-rule-select"
            >
              <option value="">— end of chain —</option>
              {rules
                .filter(r => r.id !== currentRuleId)
                .map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.enabled === false ? ' (disabled)' : ''}
                  </option>
                ))}
            </select>
          )}
          <p className="text-white/30 text-[11px] mt-1">
            When this branch fires, the engine will also evaluate the chosen rule
            (max 5 hops, cycle-safe).
          </p>
        </div>
      </div>
    </div>
  );
};
